import { useState } from "react";
import type { ActorView, LeadCardResponse } from "@gioia365/contracts";
import { Button } from "../../design/components/Button";
import { Chip } from "../../design/components/Chip";
import { Icon } from "../../design/components/Icon";
import { formatCount, formatNumericDateTime } from "../../i18n/format";
import { useLocale, useT } from "../../i18n/locale";
import { useAccess } from "../../session/access";
import type { TabContext } from "../../session/tab-context";
import { outcomeLabel } from "../lead-display";
import { useLeadView } from "../lead-queries";
import { AppointmentsSection } from "../../appointments/AppointmentsSection";
import { OpportunityBox } from "../../opportunities/OpportunityBox";
import { CallbackDialog, callsBlocked, canMoveCallback } from "./CallbackDialog";
import { nextStepText } from "./sequence-texts";

// Il «Riepilogo» fisso a destra della scheda (piano M2 Task 18; proposta 18 dell'importazione da Close, da confermare):
// «Ultima chiamata» (quando, esito, chi; entrate e uscite, decisione confermata), «Chiamate» (fatte e ricevute) e «Nel
// CRM» (creato il, da chi; ultima modifica, da chi), con «da Close» quando vengono dall'importazione. La colonna da
// comporre arriva quando avrà altro da mostrare: il prossimo passo con M5, l'opportunità con M6 (il riquadro «Opportunità», R8,
// Task 25, dopo «Ultima chiamata» come nel mock-up e in UX_SYSTEM, e sopra disco e demo; «Apri l'opportunità» apre la finestra della
// scheda). Date in numeri nel fuso dell'azienda. In cima, quando c'è, «Prossimo passo» (piano M5, Task 12; questione 264): il
// prossimo tentativo dovuto della sequenza di chiamate in corso con la sua mezza giornata, oppure «adesso»; da R3 il richiamo aperto
// viene prima (piano M5, Task 17), con l'orologio quando è concordato con il Lead e «Sposta» per chi lo può spostare, che apre la
// finestra «Richiama dopo»; con le chiamate al Lead ferme il richiamo si legge ma non si sposta, come «Richiamo» in testata
// (questione 324).

type Props = {
  tab: TabContext;
  card: LeadCardResponse;
  timeZone: string;
  /** «Apri l'opportunità» del riquadro: la finestra «Opportunità di …» della scheda. */
  onOpenOpportunity: () => void;
};

export function LeadSummary({ tab, card, timeZone, onOpenOpportunity }: Props) {
  const t = useT();
  const { locale } = useLocale();
  const access = useAccess();
  const view = useLeadView();
  const [moving, setMoving] = useState(false);
  const { stats, lead, nextStep, openTask } = card;
  const movable = nextStep?.kind === "callback" && openTask !== null && !callsBlocked(card) && canMoveCallback(access, openTask, view);
  const latest = stats.latestCall;
  const latestText = latest
    ? [
        formatNumericDateTime(timeZone, latest.at),
        latest.outcome ? outcomeLabel(t, latest.outcome) : null,
        latest.authorDisplayName,
        latest.direction === "inbound" ? t("leadCard.summary.inbound") : null,
      ]
        .filter(Boolean)
        .join(" · ")
    : t("leadCard.summary.neverCalled");
  const received = Math.max(stats.callCount - stats.outgoingCallCount, 0);

  return (
    <aside className="g-lead-summary" aria-label={t("leadCard.summary.title")}>
      <h2 className="g-lead-summary__title">{t("leadCard.summary.title")}</h2>
      {nextStep ? (
        <section className="g-lead-summary__section">
          <h3>{t("leadCard.summary.nextStep")}</h3>
          <p className="g-lead-summary__next">
            <span>{nextStepText(t, nextStep, timeZone)}</span>
            {nextStep.kind === "callback" && nextStep.agreedWithLead ? (
              <span className="g-lead-summary__agreed">
                <Icon name="clock" />
                <span className="g-visually-hidden">{t("callbacks.agreed")}</span>
              </span>
            ) : null}
          </p>
          {movable ? (
            <Button className="g-lead-summary__move" aria-haspopup="dialog" onClick={() => setMoving(true)}>
              {t("callbacks.move")}
            </Button>
          ) : null}
        </section>
      ) : null}
      {moving ? <CallbackDialog tab={tab} card={card} timeZone={timeZone} onClose={() => setMoving(false)} /> : null}
      <section className="g-lead-summary__section">
        <h3>{t("leadCard.summary.lastCall")}</h3>
        <dl>
          <div>
            <dt>{t("leadCard.summary.lastCallTerm")}</dt>
            <dd>{latestText}</dd>
          </div>
        </dl>
      </section>
      {/* L'Opportunità del Lead con la fase, il prossimo passo e «Apri l'opportunità» (piano M6, R8, Task 25; LEAD-2), poi disco e demo (R7, Task 6). */}
      <OpportunityBox tab={tab} card={card} timeZone={timeZone} onOpen={onOpenOpportunity} />
      <AppointmentsSection tab={tab} card={card} timeZone={timeZone} />
      <section className="g-lead-summary__section">
        <h3>{t("leadCard.summary.calls")}</h3>
        <dl className="g-lead-summary__pairs">
          <div>
            <dt>{t("leadCard.summary.made")}</dt>
            <dd>{formatCount(locale, stats.outgoingCallCount)}</dd>
          </div>
          <div>
            <dt>{t("leadCard.summary.received")}</dt>
            <dd>{formatCount(locale, received)}</dd>
          </div>
        </dl>
      </section>
      <section className="g-lead-summary__section">
        <h3>{t("leadCard.summary.inCrm")}</h3>
        <dl>
          <div>
            <dt>{t("leadCard.summary.created")}</dt>
            <dd>
              <Byline at={lead.createdAt} actor={lead.createdBy} timeZone={timeZone} />
            </dd>
          </div>
          <div>
            <dt>{t("leadCard.summary.updated")}</dt>
            <dd>
              <Byline at={lead.updatedAt} actor={lead.updatedBy} timeZone={timeZone} />
            </dd>
          </div>
        </dl>
      </section>
    </aside>
  );
}

/** Quando e chi, con «da Close» se viene dall'importazione. */
function Byline({ at, actor, timeZone }: { at: string; actor: ActorView; timeZone: string }) {
  const t = useT();
  const text = [formatNumericDateTime(timeZone, at), actor.displayName.trim() || null].filter(Boolean).join(" · ");
  return (
    <span className="g-lead-summary__byline">
      <span>{text}</span>
      {actor.fromClose ? <Chip tone="accent">{t("leadCard.summary.fromClose")}</Chip> : null}
    </span>
  );
}
